import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import { FormInstance } from 'antd'
import dayjs from 'dayjs'
import { State } from '../../types/StateType'
import { Patient } from './types'

const patientAdapter = createEntityAdapter<Patient>({
  selectId: (patient) => patient.patientKey,
})

let definedProps: { error: any; loading: boolean; success: any } = {
  error: null,
  loading: false,
  success: null,
}

const initialState = patientAdapter.getInitialState(definedProps)

const patientSlice = createSlice({
  name: 'patient',
  initialState: initialState,
  reducers: {
    getPatient: (state, action: PayloadAction<string>) => {
      state.loading = true
    },
    getPatients: (state) => {
      state.loading = true
    },
    addPatients: (state, action: PayloadAction<Patient[]>) => {
      state.loading = false
      patientAdapter.upsertMany(state, action.payload)
    },
    addPatient: (state, action: PayloadAction<Patient>) => {},
    addPatientSuccess: (state, action: PayloadAction<Patient>) => {
      state.loading = false
      patientAdapter.addOne(state, action.payload)
    },
    deletePatient: (state, action: PayloadAction<string>) => {},
    activatePatient: (state, action: PayloadAction<string>) => {},
    deletePatientSuccess: (state, action: PayloadAction<string>) => {
      state.loading = false
      const patientIdToDelete = action.payload
      patientAdapter.removeOne(state, patientIdToDelete)
    },
    setPatientForUpdate: (
      state,
      action: PayloadAction<{
        form: FormInstance<any>
        key: string
      }>
    ) => {},
    setPatientForUpdateSuccess: (
      state,
      action: PayloadAction<{
        form: FormInstance<any>
        patientData: Patient
      }>
    ) => {
      
      let dob = action.payload.patientData?.dob     
      action.payload.form.setFieldsValue({
        ...action.payload.patientData,
        dob: dayjs(dob),
      })
    },
    setPatientThresholdForUpdate: (
      state,
      action: PayloadAction<{
        form: FormInstance<any>
        key: string
      }>
    ) => {},
    setPatientThresholdForUpdateSuccess: (
      state,
      action: PayloadAction<{
        form: FormInstance<any>
        thresholdData: object
      }>
    ) => {
      action.payload.form.setFieldsValue({
        ...action.payload.thresholdData,
      })
    },
    updatePatientThreshold: (
      state,
      action: PayloadAction<{
        key: string
        thresholdData: object
      }>
    ) => {},
    updatePatientThresholdSuccess: (state, action: PayloadAction<object>) => {},
    updatePatient: (state, action: PayloadAction<Patient>) => {},
    updatePatientSuccess: (state, action: PayloadAction<Patient[]>) => {
      state.loading = false
      patientAdapter.upsertMany(state, action.payload)
    },
    updatePatientKey: (state, action: PayloadAction<Patient[]>) => {
      state.loading = false
      patientAdapter.upsertMany(state, action.payload)
    },
    addPatientWithPatientKey: (
      state,
      action: PayloadAction<{
        patient: Patient
        setPatientKey: (value: string) => void
      }>
    ) => {},
    errorPatient: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    successPatient: (state, action: PayloadAction<string | null>) => {
      state.success = action.payload
    },
  },
})

export const {
  errorPatient,
  getPatient,
  getPatients,
  addPatient,
  addPatientSuccess,
  updatePatient,
  updatePatientSuccess,
  deletePatient,
  deletePatientSuccess,
  updatePatientKey,
  addPatients,
  successPatient,
  activatePatient,
  setPatientForUpdate,
  setPatientForUpdateSuccess,
  setPatientThresholdForUpdate,
  setPatientThresholdForUpdateSuccess,
  updatePatientThreshold,
  addPatientWithPatientKey,
  updatePatientThresholdSuccess,
} = patientSlice.actions

export default patientSlice.reducer

const { selectById: selectByKey } = patientAdapter.getSelectors()

export const selectAllPatients = createSelector(
  (state: State) => state.patientDataState,
  (patients) => Object.values(patients['entities'])
)

export const selectPatientByKey = (id: string) =>
  createSelector(
    (state: State) => state.patientDataState,
    (patients) => selectByKey(patients, id)
  )

export const selectErrorInPatient = createSelector(
  (state: State) => state.patientDataState.error,
  (error) => error
)
export const selectSuccessInPatient = createSelector(
  (state: State) => state.patientDataState.success,
  (success) => success
)

export const selectPatientLoadingState  = createSelector(
  (state: State) => state.patientDataState.loading,
  (success) => success
)